import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@mui/material';
import { fetchBuildInfo, buildKey } from '@src/core/config';

// Detects when a new build has been deployed and prompts the user to reload,
// so stale cached bundles don't require a manual browser cache clear.
//
// How it works: every build writes build/version.json (see scripts/build.js).
// We snapshot its build key on startup and poll periodically — when the server
// reports a different key, a newer version is live. nginx already serves *.json
// with `no-store`; fetchBuildInfo adds a cache-bust param on top.

const POLL_INTERVAL = 60_000; // 1 min, same cadence as the fcs project
const FIRST_CHECK_DELAY = 5_000;
const TOAST_ID = 'app-version-update';

const UpdatePrompt: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <span>Доступна новая версия приложения</span>
    <Button size="small" variant="contained" onClick={() => window.location.reload()}>
      Обновить
    </Button>
  </div>
);

export const useVersionCheck = () => {
  const baseline = useRef<string | null>(null);
  const notified = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    let interval: ReturnType<typeof setInterval>;

    const check = async () => {
      const info = await fetchBuildInfo();
      if (!info) return;
      const key = buildKey(info);

      if (baseline.current === null) {
        baseline.current = key;
        return;
      }

      if (key !== baseline.current && !notified.current) {
        notified.current = true;
        toast.info(<UpdatePrompt />, {
          toastId: TOAST_ID,
          autoClose: false,
          closeOnClick: false,
        });
      }
    };

    const first = setTimeout(() => {
      check();
      interval = setInterval(check, POLL_INTERVAL);
    }, FIRST_CHECK_DELAY);

    return () => {
      clearTimeout(first);
      clearInterval(interval);
    };
  }, []);
};

export default useVersionCheck;
